import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { ClipboardList, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/cn";
import { Badge } from "@/components/Badge";
import { QueryError } from "@/components/QueryError";

interface AuditLog {
  id: string;
  userId?: string | null;
  action: string;
  entityType: string;
  entityId?: string | null;
  metadata?: any;
  ipAddress?: string | null;
  createdAt: string;
  user?: { firstName: string; lastName: string; email: string; role: string } | null;
}

interface AuditResponse {
  rows: AuditLog[];
  total: number;
  page: number;
  pageSize: number;
}

export function AuditLogPage() {
  const { t, i18n } = useTranslation();
  const [filters, setFilters] = useState({ userId: "", action: "", entityType: "" });
  const [page, setPage] = useState(1);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["audit-logs", filters, page],
    queryFn: async () => {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => v && params.append(k, v));
      params.append("page", String(page));
      params.append("pageSize", "50");
      return (await api.get<AuditResponse>(`/audit-logs?${params.toString()}`)).data;
    },
  });

  const totalPages = data ? Math.ceil(data.total / data.pageSize) : 1;
  const deletionsOnly = filters.action === "DELETED";

  const toggleDeletionsOnly = () => {
    setPage(1);
    setFilters({ ...filters, action: deletionsOnly ? "" : "DELETED" });
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <ClipboardList size={24} /> {t("legal.auditLog")}
        </h1>
        <button
          type="button"
          onClick={toggleDeletionsOnly}
          className={deletionsOnly ? "btn-primary" : "btn-secondary"}
        >
          <Trash2 size={16} /> {t("legal.deletionsOnly")}
        </button>
      </div>

      {deletionsOnly && (
        <div className="card flex items-center gap-3 border-l-4 border-red-500">
          <Trash2 size={20} className="text-red-600" />
          <div>
            <div className="text-2xl font-bold tabular-nums">{data?.total ?? 0}</div>
            <div className="text-sm text-slate-500">{t("legal.deletionsTally")}</div>
          </div>
        </div>
      )}

      <div className="card grid grid-cols-1 md:grid-cols-3 gap-3">
        <input className="input" placeholder={t("legal.action")} value={filters.action} onChange={(e) => { setPage(1); setFilters({ ...filters, action: e.target.value }); }} />
        <input className="input" placeholder={t("legal.entity")} value={filters.entityType} onChange={(e) => { setPage(1); setFilters({ ...filters, entityType: e.target.value }); }} />
        <input className="input" placeholder="User ID" value={filters.userId} onChange={(e) => { setPage(1); setFilters({ ...filters, userId: e.target.value }); }} />
      </div>

      <div className="card p-0 overflow-x-auto">
        {isError ? (
          <div className="p-6"><QueryError /></div>
        ) : isLoading ? (
          <div className="p-6 text-slate-500">{t("common.loading")}</div>
        ) : !data || data.rows.length === 0 ? (
          <div className="p-6 text-slate-500">—</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-4 py-2">{t("legal.when")}</th>
                <th className="text-left px-4 py-2">{t("legal.user")}</th>
                <th className="text-left px-4 py-2">{t("legal.action")}</th>
                <th className="text-left px-4 py-2">{t("legal.entity")}</th>
                <th className="text-left px-4 py-2">{t("legal.ip")}</th>
                <th className="text-left px-4 py-2">Metadata</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.rows.map((r) => (
                <tr key={r.id} className="hover:bg-slate-50 align-top">
                  <td className="px-4 py-2 whitespace-nowrap">{formatDate(r.createdAt, i18n.language)}</td>
                  <td className="px-4 py-2">
                    {r.user ? (
                      <>
                        <div className="font-medium">{r.user.firstName} {r.user.lastName}</div>
                        <div className="text-xs text-slate-500">{r.user.role}</div>
                      </>
                    ) : <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-4 py-2"><Badge variant="info">{r.action}</Badge></td>
                  <td className="px-4 py-2">
                    <div>{r.entityType}</div>
                    {r.entityId && <div className="text-xs text-slate-500 font-mono">{r.entityId.slice(0, 8)}…</div>}
                  </td>
                  <td className="px-4 py-2 text-xs text-slate-500">{r.ipAddress ?? "—"}</td>
                  <td className="px-4 py-2 text-xs text-slate-600">
                    {r.metadata ? <pre className="whitespace-pre-wrap font-mono text-xs">{JSON.stringify(r.metadata, null, 0)}</pre> : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {data && totalPages > 1 && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-slate-500">{data.total} registros · página {data.page} de {totalPages}</span>
          <div className="flex gap-2">
            <button className="btn-secondary" disabled={page <= 1} onClick={() => setPage(page - 1)}>Anterior</button>
            <button className="btn-secondary" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>Siguiente</button>
          </div>
        </div>
      )}
    </div>
  );
}
